import React, { useMemo } from "react";
import DiligentModelTooltip from "./DiligentModelTooltip";
import { describeRiskScore } from "./riskScoreDisplay";

/**
 * 14030 — "Risk Overview" card at the top of the Overview + Q&RA tabs.
 *
 * Layout (matches the design):
 *   ┌────────────────────────────────────────────────────────────────────────┐
 *   │ Risk Overview  [Diligent Model ⓘ]                                      │
 *   │                                                                        │
 *   │ [Green] 82.7%   ● 0 Critical Risk Items   ● 5 High …   ● 161 Compliant │
 *   │  Risk Score                                                  | 32 Total│
 *   │  template name…                                                Findings│
 *   └────────────────────────────────────────────────────────────────────────┘
 *
 * The card sits flat on the page (no big inner pill, no tile grid). Counts
 * are inline stat clusters with a colored dot + number + label, separated by
 * thin dividers — same visual rhythm as the rest of the page.
 */
const RiskOverviewCard = ({ scoredSnapshot, methodology, templateName, modelName = null }) => {
  const counts = useMemo(() => computeCounts(scoredSnapshot?.rows), [scoredSnapshot]);
  const modelKind = scoredSnapshot?.custom_risk_model_id ? "Custom" : "Standard";
  const totalFindings = counts.critical + counts.high + counts.medium + counts.low;

  if (!scoredSnapshot) {
    return (
      <section className="rounded-lg border border-gray-200 bg-white p-5">
        <header className="flex items-center gap-2">
          <h3 className="text-base font-semibold text-gray-900">Risk Overview</h3>
          <DiligentModelTooltip methodology={methodology} modelKind="Standard" />
        </header>
        <p className="mt-3 text-sm text-gray-500">
          No Risk Score yet. The score appears here once the questionnaire has been
          processed by the Diligent Risk Model.
        </p>
      </section>
    );
  }

  // A run nobody has scored yet says so instead of painting a vacuous score.
  const display = describeRiskScore(scoredSnapshot);

  return (
    <section className="rounded-lg border border-gray-200 bg-white p-5">
      <header className="flex items-center gap-2">
        <h3 className="text-base font-semibold text-gray-900">Risk Overview</h3>
        <DiligentModelTooltip
          methodology={methodology}
          modelKind={modelKind}
          modelName={modelName}
        />
      </header>

      <div className="mt-4 flex items-center justify-between gap-6">
        <ScoreBlock display={display} templateName={templateName} />

        <div className="flex flex-1 items-center justify-around gap-x-6 px-4">
          <InlineStat dot="bg-red-500"     count={counts.critical}  label={riskItemLabel(counts.critical, "Critical")} />
          <InlineStat dot="bg-orange-500"  count={counts.high}      label={riskItemLabel(counts.high, "High")} />
          <InlineStat dot="bg-yellow-500"  count={counts.medium}    label={riskItemLabel(counts.medium, "Medium")} />
          <InlineStat dot="bg-blue-500"    count={counts.low}       label={riskItemLabel(counts.low, "Low")} />
          <InlineStat dot="bg-emerald-500" count={counts.compliant} label={compliantLabel(counts.compliant)} />
        </div>

        <div className="pl-6 border-l border-gray-200 shrink-0">
          <div className="text-3xl font-semibold text-gray-900 tabular-nums leading-none">
            {totalFindings}
          </div>
          <div className="mt-2 text-[12px] font-medium text-gray-500">Total Findings</div>
        </div>
      </div>
    </section>
  );
};

const RAG_PILL_CLASS = {
  Green:  "bg-green-100 text-green-800 border-green-200",
  Yellow: "bg-yellow-100 text-yellow-800 border-yellow-200",
  Red:    "bg-red-100 text-red-800 border-red-200",
};

const ScoreBlock = ({ display, templateName }) => (
  <div className="flex items-center gap-3 shrink-0">
    {display.hasScore ? (
      <span
        className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-semibold ${RAG_PILL_CLASS[display.rag] || RAG_PILL_CLASS.Green}`}
      >
        {display.rag}
      </span>
    ) : (
      <span className="inline-flex items-center rounded-md border border-gray-200 bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">
        Not scored
      </span>
    )}
    <div className="leading-tight">
      <div className={`${display.hasScore ? "text-3xl" : "text-2xl"} font-bold text-gray-900 tabular-nums leading-none`}>
        {display.scoreText}
      </div>
      <div className="mt-1 text-xs font-medium text-gray-500">Risk Score</div>
      {templateName ? (
        <div className="mt-0.5 text-[11px] text-gray-400 max-w-[18rem] truncate" title={templateName}>
          {templateName}
        </div>
      ) : null}
    </div>
  </div>
);

const InlineStat = ({ dot, count, label }) => (
  <div className="flex flex-col items-center leading-tight">
    <div className="flex items-center gap-2">
      <span className={`inline-block h-2.5 w-2.5 rounded-full ${dot}`} aria-hidden="true" />
      <span className="text-2xl font-semibold text-gray-900 tabular-nums leading-none">
        {count}
      </span>
    </div>
    <span className="mt-1.5 text-[13px] text-gray-600 whitespace-nowrap">{label}</span>
  </div>
);

// "Critical Risk Item" when count === 1, "Critical Risk Items" otherwise.
const riskItemLabel = (count, tier) => `${tier} Risk Item${count === 1 ? "" : "s"}`;
const compliantLabel = (count) => `Compliant Response${count === 1 ? "" : "s"}`;

/**
 * Aggregate counts over the per-question scored rows. Mutually exclusive
 * buckets — each row lands in exactly one of critical / high / medium /
 * low / compliant / notScored. Matches the canonical routing in the
 * bottom VqqStatsBar so the two surfaces always agree.
 *
 * Rules (in priority order):
 *   1. SME override (sme_tier) wins over auto.
 *      - "Compliant" → compliant
 *      - "Not Scored" → notScored
 *      - Low / Medium / High / Critical → that severity bucket
 *   2. No SME override → finding_type.
 *      - compliant / notapplicable → compliant
 *      - empty / notscored / undetermined / outofscope → notScored
 *      - else → severity bucket via auto_final_risk (defaults to Low)
 */
const computeCounts = (rows) => {
  const out = { critical: 0, high: 0, medium: 0, low: 0, compliant: 0, notScored: 0 };
  if (!Array.isArray(rows)) return out;
  for (const r of rows) {
    const finding = String(r?.finding_type || "").toLowerCase();
    const smeTouched =
      (r?.sme_reviewer_email && String(r.sme_reviewer_email).trim().length > 0) ||
      (r?.sme_tier && String(r.sme_tier).trim().length > 0) ||
      r?.sme_score != null ||
      (r?.sme_review_notes && String(r.sme_review_notes).trim().length > 0);
    const smeTier = String(r?.sme_tier || "").trim().toLowerCase();
    const tier = (smeTouched && smeTier) || String(r?.auto_final_risk || "").toLowerCase();

    const smeCompliant = smeTier === "compliant";
    const smeNotScored = smeTier === "not scored" || smeTier === "notscored";
    const autoCompliant = finding === "compliant" || finding === "notapplicable";
    const autoNotScored =
      finding === "" || finding === "notscored" ||
      finding === "undetermined" || finding === "outofscope";

    if (smeCompliant || (!smeTouched && autoCompliant)) {
      out.compliant += 1;
    } else if (smeNotScored || (!smeTouched && autoNotScored)) {
      out.notScored += 1;
    } else if (tier === "critical") {
      out.critical += 1;
    } else if (tier === "high") {
      out.high += 1;
    } else if (tier === "medium") {
      out.medium += 1;
    } else {
      out.low += 1;
    }
  }
  return out;
};

export default RiskOverviewCard;
